import { useState } from "react";
import { snackbarHelperActions } from "src/components/20-helper-views/snackbar/useSnackbar";
import useStableCallback from "src/hooks/useCallbackUtils/useStableCallback";
import { useRearrangeNoteTabsMutation } from "src/modules/exam-preparation/graphql/noteTabs/rearrange";
import { useUpdateNoteTabMutation } from "src/modules/exam-preparation/graphql/noteTabs/update";
import useI18n from "src/modules/translations/hooks/useI18n";
import useNoteTabActions from "./hooks/useNoteTabActions";
import useNoteTabsDraft from "./hooks/useNoteTabsDraft";
export default function useData(note) {
    const [activeTabID, setActiveTabID] = useState(note.tabs?.[0]?.id ?? '');
    const [, updateNoteTabMutationActions] = useUpdateNoteTabMutation();
    const [rearrangeNoteTabsMutationState, rearrangeNoteTabsMutationActions] = useRearrangeNoteTabsMutation();
    const [syncingTabIDs, setSyncingTabIDs] = useState(new Set());
    const { translate } = useI18n();
    const [draftState, draftActions, draftHelpers] = useNoteTabsDraft(note);
    const [, noteTabActions] = useNoteTabActions(note);
    const addTab = useStableCallback(() => {
        noteTabActions.addTab((newTab) => {
            setActiveTabID(newTab.id);
            draftActions.setTabContent(newTab.id, newTab.data.content ?? '');
        });
    });
    const editTab = useStableCallback((tab) => {
        noteTabActions.editTab(tab);
    });
    const deleteTab = useStableCallback((tab) => {
        noteTabActions.deleteTab(tab, () => {
            if (activeTabID === tab.id) {
                const remainingTabs = note.tabs.filter(t => t.id !== tab.id);
                if (remainingTabs.length > 0) {
                    setActiveTabID(remainingTabs[0].id);
                }
                else {
                    setActiveTabID('');
                }
            }
            draftActions.deleteTabContent(tab.id);
        });
    });
    const hasTabChanges = useStableCallback((tab) => {
        const draft = draftState.get(tab.id);
        return draft !== undefined && draft !== (tab.data.content ?? '');
    });
    const hasUnsavedChanges = useStableCallback(() => {
        return note.tabs.some((tab) => hasTabChanges(tab));
    });
    const save = useStableCallback(async () => {
        const changedTabs = draftHelpers.getChangedTabs();
        if (changedTabs.length > 0) {
            setSyncingTabIDs(new Set(changedTabs.map(({ tabID }) => tabID)));
            try {
                await Promise.all(changedTabs.map(({ tabID, content, version }) => updateNoteTabMutationActions.submit({
                    variables: {
                        noteTabID: tabID,
                        data: {
                            content,
                            version,
                        },
                    },
                })));
                snackbarHelperActions.show({
                    variant: 'POSITIVE',
                    duration: 'SHORT',
                    message: translate('Changes saved successfully'),
                });
            }
            finally {
                setSyncingTabIDs(new Set());
            }
        }
    });
    const rearrangeTabs = useStableCallback(async (noteTabIDs) => {
        await rearrangeNoteTabsMutationActions.submit({
            variables: {
                noteID: note.id,
                data: { noteTabIDs },
            },
        });
    });
    const state = {
        activeTabID,
        draft: draftState,
        syncingTabIDs,
        rearrangeNoteTabsMutation: rearrangeNoteTabsMutationState,
    };
    const actions = {
        setActiveTabID,
        addTab,
        editTab,
        deleteTab,
        rearrangeTabs,
        draft: draftActions,
        save,
        hasUnsavedChanges,
    };
    return [state, actions];
}
